import type { User } from '@/shared/types'

const STAFF_ROLES = new Set(['hr_operator', 'hr', 'recruitment_staff'])

const ROLE_ALIASES: Record<string, string> = {
  admin: 'tenant_admin',
  system_admin: 'tenant_admin',
  owner: 'boss',
  management_viewer: 'boss',
  hr: 'hr_operator',
  recruitment_staff: 'hr_operator',
  'team-lead': 'teamlead_viewer',
  recruitment_teamlead: 'teamlead_viewer',
}

/** Trưởng nhóm, trưởng phòng, admin, boss — không nhận lead. */
const BLOCKED_ROLES = new Set([
  'teamlead_viewer',
  'recruitment_manager',
  'tenant_admin',
  'workspace_admin',
  'platform_super_admin',
  'boss',
  'business_teamlead',
  'supplier_channel_teamlead',
  'onsite_manager',
  'business_manager',
  'finance_manager',
  'operation_manager',
  'marketing_manager',
  'supplier_admin',
])

function resolveRole(name: string): string {
  return ROLE_ALIASES[name] ?? name
}

function roleNames(user: Pick<User, 'roles' | 'assigned_roles'>): string[] {
  return [...(user.roles ?? []), ...(user.assigned_roles ?? [])]
}

function roleIsStaff(name: string): boolean {
  const resolved = resolveRole(name)
  return STAFF_ROLES.has(name) || STAFF_ROLES.has(resolved)
}

function roleIsOversight(name: string): boolean {
  const resolved = resolveRole(name)
  return BLOCKED_ROLES.has(name) || BLOCKED_ROLES.has(resolved)
}

/** Admin, trưởng nhóm, trưởng phòng, boss — không làm pipeline cá nhân của TVV. */
export function isRecruitmentOversightUser(
  user: Pick<User, 'roles' | 'assigned_roles' | 'is_platform_super_admin' | 'is_workspace_admin' | 'is_tenant_admin'> | null | undefined,
): boolean {
  if (!user) return false
  if (user.is_platform_super_admin || user.is_workspace_admin || user.is_tenant_admin) return true
  return roleNames(user).some(roleIsOversight)
}

/** Same gate as backend `RecruitmentScope::canMonitorStaffChats` — view staff Zalo, never act. */
const ZALO_MONITOR_ROLES = new Set([
  'teamlead_viewer',
  'recruitment_manager',
  'tenant_admin',
  'workspace_admin',
  'platform_super_admin',
  'boss',
])

export function canMonitorZaloStaffChats(
  user: Pick<User, 'roles' | 'assigned_roles' | 'is_platform_super_admin' | 'is_workspace_admin' | 'is_tenant_admin'> | null | undefined,
): boolean {
  if (!user) return false
  if (user.is_platform_super_admin || user.is_workspace_admin || user.is_tenant_admin) return true
  return roleNames(user).some((name) => {
    const resolved = resolveRole(name)
    return ZALO_MONITOR_ROLES.has(name) || ZALO_MONITOR_ROLES.has(resolved)
  })
}

/** Same roles as backend `LeadNotificationAudience::oversees`. */
const LEAD_WARNING_OVERSIGHT_ROLES = new Set([
  'teamlead_viewer',
  'recruitment_manager',
  'operation_operator',
  'operation_manager',
  'tenant_admin',
  'workspace_admin',
  'platform_super_admin',
])

/** Trưởng nhóm, trưởng phòng tuyển dụng, vận hành, admin — thấy cảnh báo lead của cấp dưới. Boss không thấy. */
export function overseesLeadWarnings(
  user: Pick<User, 'roles' | 'assigned_roles' | 'is_platform_super_admin' | 'is_workspace_admin' | 'is_tenant_admin'> | null | undefined,
): boolean {
  if (!user) return false
  if (roleNames(user).some((name) => resolveRole(name) === 'boss')) return false
  if (user.is_platform_super_admin || user.is_workspace_admin || user.is_tenant_admin) return true
  return roleNames(user).some((name) => LEAD_WARNING_OVERSIGHT_ROLES.has(resolveRole(name)))
}

/** Chỉ nhân viên phòng tuyển dụng thấy công tắc «Đang nhận nguồn». */
export function userReceivesLeadDistribution(user: Pick<User, 'roles' | 'assigned_roles' | 'is_platform_super_admin' | 'is_workspace_admin' | 'is_tenant_admin'> | null | undefined): boolean {
  if (!user || isRecruitmentOversightUser(user)) return false
  return roleNames(user).some(roleIsStaff)
}

/** Trang chỉ của một nhân viên — cấp trên không vào, họ xem phạm vi cấp dưới ở các trang còn lại. */
const PERSONAL_RECRUITMENT_ROUTES = new Set([
  'recruiter-my-commission',
  'lead-library-personal',
  'inbox',
])

export function isPersonalRecruitmentRoute(routeName: string | null | undefined): boolean {
  return PERSONAL_RECRUITMENT_ROUTES.has(String(routeName ?? ''))
}
